import { MoreHorizontal, Trash2, Edit2, Undo2 } from "lucide-react";
import { TableActionMenu } from "../ui/TableActionMenu";
import { useState } from "react";
import { Card } from "../ui/Card";
import { useAuth } from "../../contexts/AuthContext";

interface Payment {
  _id: string;
  user?: {
    _id: string;
    username?: string;
    email?: string;
  } | null;
  subscription?: {
    _id: string;
    name?: string;
    displayName?: string;
    price?: number;
  } | null;
  amount: number;
  currency: string;
  transactionId: string;
  paytabsTranRef?: string;
  status: "pending" | "completed" | "failed" | "refunded";
  paymentMethod: string;
  createdAt: string;
}

interface PaymentsListTableProps {
  payments: Payment[];
  isLoading: boolean;
  onEdit: (payment: Payment) => void;
  onDelete: (payment: Payment) => void;
  onRefund?: (payment: Payment) => void;
  totalItems: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export function PaymentsListTable({
  payments,
  isLoading,
  onEdit,
  onDelete,
  onRefund,
  totalItems,
  currentPage,
  pageSize,
  totalPages,
  onPageChange,
}: PaymentsListTableProps) {
  const { hasPermission } = useAuth();

  const canUpdateStatus = hasPermission('payments:update-status');
  const canDelete = hasPermission('payments:delete');

  const [activeMenu, setActiveMenu] = useState<{
    id: string;
    position: { top: number; right: number };
  } | null>(null);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
        return "bg-green-100 text-green-700";
      case "pending":
        return "bg-yellow-100 text-yellow-700";
      case "failed":
        return "bg-red-100 text-red-700";
      case "refunded":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const getPaymentMethodLabel = (method: string) => {
    switch (method) {
      case "credit_card":
        return "Credit Card";
      case "bank_transfer":
        return "Bank Transfer";
      case "paytabs":
        return "Card (PayTabs)";
      case "cash":
        return "Cash";
      case "package":
        return "Package";
      case "legacy":
        return "Legacy";
      default:
        return method;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (isLoading) {
    return (
      <Card className="p-0 border shadow-sm">
        <div className="p-8 text-center text-gray-500">
          <div className="animate-spin inline-block w-6 h-6 border-[3px] border-current border-t-transparent text-indigo-600 rounded-full" />
          <p className="mt-2">Loading payments...</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-0 border shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-[1000px] w-full text-left text-sm border-separate border-spacing-0">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100">
                Transaction ID
              </th>
              <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100">
                User
              </th>
              <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100">
                Subscription
              </th>
              <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100">
                Amount
              </th>
              <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100">
                Payment Method
              </th>
              <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100">
                Date
              </th>
              <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100">
                Status
              </th>
              <th className="px-6 py-4 w-[50px] bg-gray-50 border-b border-gray-100"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {payments.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-6 py-12 text-center text-gray-500"
                >
                  No payments found.
                </td>
              </tr>
            ) : (
              payments.map((item) => (
                <tr
                  key={item._id}
                  className="hover:bg-gray-50/50 transition-colors"
                >
                  <td className="px-6 py-4">
                    <span className="font-mono text-xs text-gray-600">
                      {item.transactionId}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col">
                      <span className="font-medium text-[#102040]">
                        {item.user?.username || "Unknown User"}
                      </span>
                      <span className="text-xs text-gray-400">
                        {item.user?.email || "No email"}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 font-medium text-[#102040]">
                    {item.subscription?.displayName ||
                      item.subscription?.name ||
                      "N/A"}
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-semibold text-[#102040]">
                      {item.amount} {item.currency}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    <div className="flex items-center gap-2">
<span>{getPaymentMethodLabel(item.paymentMethod)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-[#989797]">
                    {formatDate(item.createdAt)}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(
                        item.status,
                      )}`}
                    >
                      {item.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right relative">
                    <button
                      className="p-1 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (activeMenu?.id === item._id) {
                          setActiveMenu(null);
                        } else {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setActiveMenu({
                            id: item._id,
                            position: {
                              top: rect.bottom,
                              right: window.innerWidth - rect.right,
                            },
                          });
                        }
                      }}
                    >
                      <MoreHorizontal size={20} />
                    </button>

                    <TableActionMenu
                      isOpen={activeMenu?.id === item._id}
                      onClose={() => setActiveMenu(null)}
                      position={activeMenu?.position || null}
                    >
                      {canUpdateStatus && (
                        <button
                          onClick={() => {
                            onEdit(item);
                            setActiveMenu(null);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                        >
                          <Edit2 size={14} className="text-blue-500" />
                          Edit Status
                        </button>
                      )}                      {item.paymentMethod === "paytabs" && onRefund && (
                        <button
                          onClick={() => {
                            onRefund(item);
                            setActiveMenu(null);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                        >
                          <Undo2 size={14} className="text-purple-500" />
                          Refund PayTabs Payment
                        </button>
                      )}
{canDelete && (
                        <button
                          onClick={() => {
                            onDelete(item);
                            setActiveMenu(null);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      )}
                    </TableActionMenu>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalItems > 0 && (
        <div className="px-3 sm:px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-2 text-sm text-gray-500">
          <span>
            Showing {Math.min((currentPage - 1) * pageSize + 1, totalItems)} to{" "}
            {Math.min(currentPage * pageSize, totalItems)} of {totalItems}{" "}
            payments
          </span>
          <div className="flex gap-1 ml-4">
            <button
              className="px-2 py-1 hover:bg-gray-100 rounded disabled:opacity-50"
              disabled={currentPage === 1}
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            >
              &lt; Previous
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let p = i + 1;
              if (totalPages > 5 && currentPage > 3) {
                p = currentPage - 2 + i;
              }
              if (p > totalPages) return null;

              return (
                <button
                  key={p}
                  onClick={() => onPageChange(p)}
                  className={`px-3 py-1 rounded ${
                    currentPage === p
                      ? "bg-gray-900 text-white"
                      : "hover:bg-gray-100"
                  }`}
                >
                  {p}
                </button>
              );
            })}
            <button
              className="px-2 py-1 hover:bg-gray-100 rounded disabled:opacity-50"
              disabled={currentPage === totalPages}
              onClick={() =>
                onPageChange(Math.min(totalPages, currentPage + 1))
              }
            >
              Next &gt;
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}
